import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
} from "@elaniintc/goes-design-system";
import { VariableSelector } from "./VariableSelector";
import { OperationSelector } from "./OperationSelector";
import { ComparativeValueInput } from "./ComparativeValueInput";
import { SubCondition } from "./SubCondition";
import type { ConditionProps, Variable } from "./types";
import { useCondition } from "./useCondition";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useEffect } from "react";
import type { ApiNode } from "@/workflow_api/types/workflow";
import { IfNameInputs } from "./NameInputs";
import { ComparativeValueInputArray } from "./ComparativeValueInput.array";
import { parseArrayValue } from "./utils";

const Condition = ({ variables }: ConditionProps) => {
  const {
    selectedVariable,
    selectedOperation,
    logicalOperator,
    additionalConditions,
    comparativeValue,
    handleVariableChange,
    handleOperationChange,
    setComparativeValue,
    setLogicalOperator,
    addCondition,
    updateCondition,
    deleteCondition,
    isAddConditionDisabled,
    comparativeValueType,
    setComparativeValueType,
    setSerializedNode,
  } = useCondition();
  const { selectedNodeId } = useUIStore();
  const { getNodeById } = useWorkflowStore();
  const node = getNodeById(selectedNodeId || "");

  useEffect(() => {
    if (node && node.data) {
      const allVariables = variables.flatMap((v) => {
        return v.variables.map((variable) => {
          return {
            id: variable.id,
            name: variable.name,
            type: variable.type,
            node_id: v.nodeId,
            ...(variable.field_id && { field_id: variable.field_id }),
            ...(variable.output_name && { output_name: variable.output_name }),
            ref_name: v.ref_name || v.type,
          };
        });
      });

      setSerializedNode(
        allVariables as Variable[],
        node.data as unknown as ApiNode,
      );
    }
  }, [node, setSerializedNode, variables]);

  return (
    <div className="w-full flex flex-col gap-3 py-3">
      <div className="flex flex-col gap-6 px-6 pt-3 pb-6 border-b border-neutral-quiet">
        <GroupTextField label="Condición" required className="w-full">
          <GroupTextFieldRow>
            <div className="w-full flex">
              <VariableSelector
                variables={variables}
                selectedVariable={selectedVariable}
                onSelectVariable={handleVariableChange}
                triggerGroup={
                  selectedOperation?.needsComparativeValue &&
                  selectedOperation?.comparativeValueType !== "array"
                    ? "up-left"
                    : "left"
                }
              />
              <OperationSelector
                selectedVariable={selectedVariable}
                selectedOperation={selectedOperation}
                onSelectOperation={handleOperationChange}
                group={
                  selectedOperation?.needsComparativeValue &&
                  selectedOperation?.comparativeValueType !== "array"
                    ? "up-right"
                    : "right"
                }
                disabled={!selectedVariable}
              />
            </div>
          </GroupTextFieldRow>

          {selectedOperation?.needsComparativeValue &&
            (selectedOperation?.comparativeValueType === "array" ? (
              <GroupTextFieldRow className="h-auto">
                <ComparativeValueInputArray
                  variables={variables}
                  value={parseArrayValue(comparativeValue)}
                  onChange={(newValue) => setComparativeValue(newValue)}
                />
              </GroupTextFieldRow>
            ) : (
              <GroupTextFieldRow>
                <Button
                  variant="quiet"
                  type="button"
                  className="font-mono"
                  data-testid="toggle-comparative-value-type-btn"
                  onClick={() => {
                    setComparativeValueType(
                      comparativeValueType === "input" ? "variable" : "input",
                    );
                    setComparativeValue("");
                  }}
                >
                  {comparativeValueType === "input" ? "inp" : "var"}
                </Button>
                <ComparativeValueInput
                  selectedVariable={selectedVariable}
                  selectedOperation={{
                    ...selectedOperation,
                    comparativeValueType:
                      comparativeValueType === "variable"
                        ? "variable"
                        : selectedOperation.comparativeValueType,
                  }}
                  comparativeValue={comparativeValue}
                  onValueChange={setComparativeValue}
                  variables={variables}
                />
              </GroupTextFieldRow>
            ))}
        </GroupTextField>
        {additionalConditions.map((condition) => {
          const {
            id,
            variable,
            comparativeValue,
            operation,
            comparativeValueType,
          } = condition;

          return (
            <SubCondition
              key={id}
              id={id ?? ""}
              mainVariableType={selectedVariable?.type || ""}
              logicalOperator={logicalOperator}
              comparativeValueType={comparativeValueType ?? "input"}
              onDelete={() => id && deleteCondition(id)}
              setComparativeValueType={(type) =>
                id && updateCondition(id, { comparativeValueType: type })
              }
              onSelectOperation={(op) =>
                id && updateCondition(id, { operation: op })
              }
              onSelectVariable={(variable) => {
                if (id) {
                  updateCondition(id, {
                    variable,
                    operation: null,
                    comparativeValue: "",
                  });
                }
              }}
              setLogicalOperator={setLogicalOperator}
              variables={variables}
              variable={variable}
              comparativeValue={
                comparativeValue ??
                (operation?.comparativeValueType === "array" ? [] : "")
              }
              operation={operation}
              setComparativeValue={(val) => {
                if (id) {
                  updateCondition(id, { comparativeValue: val });
                }
              }}
            />
          );
        })}

        <div>
          <Button
            type="button"
            variant="tertiary"
            disabled={isAddConditionDisabled}
            onClick={addCondition}
          >
            Añadir condición
            <Icon type="far" name="plus" />
          </Button>
        </div>
      </div>
      <IfNameInputs />
    </div>
  );
};

export default Condition;
